/**
 * Vanilla JS marquee
 */

export interface MarqueeOptions {
  texts: string | string[]          // single text or queue of texts
  defaultText?: string | (() => string)             // shown between each item in the queue
  
  speed?: number
  direction?: 'left' | 'right' | 'up' | 'down'
  duration?: number
  gap?: number
  delayBeforeStart?: number
  pauseOnHover?: boolean
  duplicated?: boolean
  repeat?: number
  repeatSeparator?: string
}

const DEFAULT_MARQUEE_CONFIG: Required<Omit<MarqueeOptions, 'texts' | 'defaultText'>> = {
  speed: 50,
  direction: 'left',
  duration: 5000,
  gap: 0,
  delayBeforeStart: 0,
  pauseOnHover: false,
  duplicated: true,
  repeat: 2,
  repeatSeparator: ' — '
}

export class Marquee {
  private element: HTMLElement
  private options: Required<Omit<MarqueeOptions,'texts'|'defaultText'>> & {
    texts: string | string[]
    defaultText?: string | (() => string)
  }
  private wrapper: HTMLElement | null = null
  private content: HTMLElement | null = null
  private animationId: number | null = null
  private isRunning: boolean = false
  private isPaused: boolean = false
  private startTime: number = 0
  private pausedTime: number = 0

  constructor(element: HTMLElement, options: MarqueeOptions) {
    this.element = element
    this.options = { 
      ...DEFAULT_MARQUEE_CONFIG, 
      ...options,
    } as typeof this.options

  }

  /* -------------------------------------------------------------------------
  |  PUBLIC API
  |------------------------------------------------------------------------- */

  start(): void {
    if (this.isRunning) return

    this.setupDOM()

    if (this.options.delayBeforeStart > 0) {
      setTimeout(() => this.startAnimation(), this.options.delayBeforeStart)
    } else {
      this.startAnimation()
    }
  }

  stop(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }
    this.isRunning = false
    this.isPaused = false
  }

  pause(): void {
    if (!this.isRunning || this.isPaused) return
    this.isPaused = true
    this.pausedTime = Date.now()
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }
  }

  resume(): void {
    if (!this.isRunning || !this.isPaused) return
    this.isPaused = false
    const pauseDuration = Date.now() - this.pausedTime
    this.startTime += pauseDuration
    this.animate()
  }

  toggle(): void {
    this.isPaused ? this.resume() : this.pause()
  }

  destroy(): void {
    this.stop()

    if (this.wrapper) {
      this.wrapper.removeEventListener('mouseenter', this.pause)
      this.wrapper.removeEventListener('mouseleave', this.resume)
    }
    this.element.innerHTML = ''
    
    this.wrapper = null
    this.content = null
  }

  /**
   * Replace text/queue and restart from the beginning
   */
  updateText(newText: string | string[], newDefaultText?: string): void {
    const wasRunning = this.isRunning
    this.stop()
    this.options.texts = newText
    if (newDefaultText !== undefined) this.options.defaultText = newDefaultText
    if (wasRunning) this.start()
  }

  updateConfig(newOptions: Partial<MarqueeOptions>): void {
    this.options = { ...this.options, ...newOptions } as typeof this.options
    if (this.isRunning) {
      this.stop()
      this.start()
    }
  }

  /* -------------------------------------------------------------------------
  |  D O M
  |------------------------------------------------------------------------- */

  private setupDOM(): void {
    this.element.innerHTML = ''

    this.wrapper = document.createElement('div')
    this.wrapper.style.overflow = 'hidden'
    this.wrapper.style.position = 'relative'
    this.wrapper.style.width = '100%'
    this.wrapper.style.height = '100%'

    this.content = document.createElement('div')
    this.content.style.display = 'inline-block'
    this.content.style.whiteSpace = 'nowrap'
    this.content.style.position = 'relative'

    const prepared = this.prepareText(this.buildText())
    this.content.innerHTML = prepared

    if (this.options.duplicated) {
      const duplicate = document.createElement('span')
      duplicate.style.marginLeft = this.options.gap + 'px'
      duplicate.innerHTML = prepared
      this.content.appendChild(duplicate)
    }

    this.wrapper.appendChild(this.content)
    this.element.appendChild(this.wrapper)


    if (this.options.pauseOnHover && this.wrapper) {
      this.wrapper.addEventListener('mouseenter', this.handleMouseEnter)
      this.wrapper.addEventListener('mouseleave', this.handleMouseLeave)
    }
  }

  private handleMouseEnter = () => this.pause()
  private handleMouseLeave = () => this.resume()

  /* ------------------------------------------------------------------ */
  /* TEXT GENERATION                                                     */
  /* ------------------------------------------------------------------ */

  private resolveDefaultText(): string {
    const dt = this.options.defaultText
    if (!dt) return ''
    return typeof dt === 'function' ? dt() : dt
  }

  private buildText(): string {

    const texts = Array.isArray(this.options.texts)
      ? this.options.texts.filter(Boolean)
      : [this.options.texts]

    const defaultText = this.resolveDefaultText()

    if (texts.length === 0)
      return defaultText

    const parts: string[] = []

    texts.forEach(t => {

      if (defaultText)
        parts.push(defaultText)

      parts.push(t)
    })

    if (defaultText)
      parts.push(defaultText)

    return parts.join(this.options.repeatSeparator)
  }

  private prepareText(text: string): string {

    if (this.options.repeat <= 1)
      return text

    return Array(this.options.repeat)
      .fill(text + ' ')           // ← space after each text
      .join(this.options.repeatSeparator)
      .trimEnd();                 // optional: remove very last space if unwanted
  }

  /* ------------------------------------------------------------------ */
  /* ANIMATION                                                           */
  /* ------------------------------------------------------------------ */

  private startAnimation(): void {
    if (!this.content || !this.wrapper) return

    this.isRunning = true
    this.isPaused = false
    this.startTime = Date.now()

    this.animate()
  }

  private animate = (): void => {
    if (!this.isRunning || this.isPaused || !this.content || !this.wrapper) return

    const now = Date.now()
    const elapsed = (now - this.startTime) / 1000
    const distance = this.options.speed * elapsed

    let newPosition = 0
    let isFinished = false

    if (this.options.direction === 'left') {
      newPosition = this.wrapper.offsetWidth - distance
      this.content.style.transform = `translateX(${newPosition}px)`
      if (newPosition <= -this.content.offsetWidth / (this.options.duplicated ? 2 : 1)) {
        isFinished = true
      }
    } else if (this.options.direction === 'right') {
      newPosition = -this.content.offsetWidth + distance
      this.content.style.transform = `translateX(${newPosition}px)`
      if (newPosition >= this.wrapper.offsetWidth) {
        isFinished = true
      }
    } else if (this.options.direction === 'up') {
      newPosition = this.wrapper.offsetHeight - distance
      this.content.style.transform = `translateY(${newPosition}px)`
      if (newPosition <= -this.content.offsetHeight / (this.options.duplicated ? 2 : 1)) {
        isFinished = true
      }
    } else if (this.options.direction === 'down') {
      newPosition = -this.content.offsetHeight + distance
      this.content.style.transform = `translateY(${newPosition}px)`
      if (newPosition >= this.wrapper.offsetHeight) {
        isFinished = true
      }
    }

    if (isFinished) {
      if (this.animationId !== null) {
        cancelAnimationFrame(this.animationId)
        this.animationId = null
      }
      this.isRunning = false  // ← reset so startAnimation doesn't bail

      // Rebuild DOM (re-evaluates defaultText function) then restart
      requestAnimationFrame(() => {
        this.setupDOM()
        this.startAnimation()
      })
      return

    }
    this.animationId = requestAnimationFrame(this.animate)
  
  }

}